import {useState} from 'react'
import {Link, useNavigate} from 'react-router'

import {Header as CoreHeader, type HeaderSearchSlot} from '@core/wall/ui/Header'
import {UserBlock} from '@core/auth-ui/UserBlock/UserBlock'
import {useModelStatus} from '@core/hooks/useModelStatusProvider'

import {NarcisMark} from './NarcisMark'
import {modelMenuLabel} from './modelMenuLabel'
import styles from './header.module.scss'
import {intakeFromLink} from '@product/narcis/path'

/** What the wall hands the header: its `q` and the setter (`Wall.tsx`'s `renderHeader`). */
export type HeaderSearch = HeaderSearchSlot

/** The door the page under this header belongs to, lit in the nav; `null` on the wall itself. */
export type WallHeaderAccent = 'packs' | 'tools' | null

/**
 * NarcisWallHeader — Narcis's slots on the shared wall header. The avatar menu's "Your model" row
 * is the only link to `/model` the wall surface carries (operator, 2026-09-21: parked under the
 * avatar, nothing redirects to it). Mounted on the wall itself (`search` is the live `q`) and, via
 * `components/Header/header.tsx`, on every other narcis page with `usePageSearch()`.
 */
export function NarcisWallHeader({search, accent = null}: {search?: HeaderSearch; accent?: WallHeaderAccent}) {
    const modelStatus = useModelStatus()
    return (
        <CoreHeader
            brand={
                <>
                    <NarcisMark size={28} />
                    <span className={styles.word}>Narcis</span>
                </>
            }
            search={search}
            nav={
                <>
                    <Link to="/packs" className={accent === 'packs' ? styles.navLinkOn : styles.navLink}>
                        Packs
                    </Link>
                    <Link to="/tools" className={accent === 'tools' ? styles.navLinkOn : styles.navLink}>
                        Tools
                    </Link>
                </>
            }
            right={
                <UserBlock
                    extraLinks={[
                        {href: intakeFromLink('header'), label: modelMenuLabel(modelStatus)},
                        {href: '/packs', label: 'Packs', phoneOnly: true},
                        {href: '/tools', label: 'Tools', phoneOnly: true},
                    ]}
                    signIn={{href: '/auth/login'}}
                />
            }
        />
    )
}

/** The header's search on a page with no wall under it: Enter re-enters `/?q=`. */
export function usePageSearch(): HeaderSearch {
    const [value, setValue] = useState('')
    const navigate = useNavigate()
    return {
        value,
        onChange: setValue,
        placeholder: 'What do you want to see?',
        onSubmit: (q) => {
            const words = q.trim()
            navigate(words ? `/?q=${encodeURIComponent(words)}` : '/')
        },
    }
}
